import { range } from 'lodash-es';

import imagePreloader, { type TaskResult } from './image-preloader';

export enum RotationDirection {
  Clockwise = 'clockwise',
  Counterclockwise = 'counterclockwise'
}

const DEFAULT_INIT_LOOKAHEAD = 5;
const DEFAULT_ROTATION_LOOKAHEAD = 10;

export interface ImagePreloadConfig {
  concurrency: number;
  initLookaheadEachSide: number;
  rotationLookahead: number;
}

class ImagePreloadScheduler {
  private initLookaheadEachSide: number = DEFAULT_INIT_LOOKAHEAD;
  private rotationLookahead: number = DEFAULT_ROTATION_LOOKAHEAD;

  public configure({
    concurrency,
    initLookaheadEachSide: initLookaheadEachSide,
    rotationLookahead
  }: ImagePreloadConfig): void {
    imagePreloader.updateConcurrency(concurrency);
    this.initLookaheadEachSide = initLookaheadEachSide;
    this.rotationLookahead = rotationLookahead;
  }

  public async scheduleForInit(
    index: number,
    perspectives: number,
    urlCreator: (index_: number) => Promise<string>
  ): Promise<(void | TaskResult)[]> {
    const indexPackages: number[][] = range(1, this.initLookaheadEachSide + 1, 1).reduce<number[][]>(
      (acc: number[][], current: number) => [
        ...acc,
        [index + current, index - current].map(idx => this.normalizeIndex(idx, perspectives))
      ],
      []
    );
    return this.preload(indexPackages, urlCreator);
  }

  public async scheduleForInteractiveRotation(
    index: number,
    rotationDirection: RotationDirection,
    perspectives: number,
    urlCreator: (index_: number) => Promise<string>
  ): Promise<(void | TaskResult)[]> {
    const rotationDirectionFactor = this.getRotationFactorByDirection(rotationDirection);

    const indexRangeArray = range(
      index,
      index + this.rotationLookahead * rotationDirectionFactor,
      rotationDirectionFactor
    ).map(idx => this.normalizeIndex(idx, perspectives));
    return this.preload([indexRangeArray], urlCreator);
  }

  public async scheduleForAutomaticRotation(
    currentIndex: number,
    targetIndex: number,
    perspectives: number,
    urlCreator: (index: number) => Promise<string>
  ): Promise<{ result: (void | TaskResult)[]; indexRangeArray: number[] }> {
    /**
     * Creates an array of indexes for the shortest way from currentIndex to targetIndex.
     * When currentIndex is e.g. 5 and target index 10, the array would look like [6, 7, 8, 9, 10].
     * When currentIndex is e.g. 2 and target index 33, the array would look like [1, 0, 35, 34, 33].
     */
    const createIndexArray = (currentIdx: number, targetIdx: number, numberOfImages: number): number[] => {
      if (targetIdx < currentIdx) {
        targetIdx += numberOfImages;
      }

      const steps = targetIdx - currentIdx;
      const halfRoundSteps = numberOfImages / 2;
      const rotationDirection: 'clockwise' | 'counterclockwise' =
        steps < halfRoundSteps ? 'counterclockwise' : 'clockwise';

      if (rotationDirection === 'clockwise') {
        targetIdx -= numberOfImages;
        if (targetIdx < 0) {
          targetIdx += numberOfImages;
        }
      }

      const step = rotationDirection === 'counterclockwise' ? 1 : -1;
      return range(currentIdx + step, targetIdx + step, step).map((index: number) => index % numberOfImages);
    };

    const indexRangeArray = createIndexArray(currentIndex, targetIndex, perspectives);
    const result = await this.preload([indexRangeArray], urlCreator);
    return { result, indexRangeArray };
  }

  private getRotationFactorByDirection(rotationDirection: RotationDirection): number {
    return rotationDirection === RotationDirection.Clockwise ? -1 : 1;
  }

  private normalizeIndex(index: number, perspectives: number): number {
    let x = index;
    x = x < 0 ? x + perspectives : x;
    x = x >= perspectives ? x - perspectives : x;
    return x;
  }

  private async preload(
    indexPackages: number[][],
    urlCreator: (index: number) => Promise<string>
  ): Promise<(void | TaskResult)[]> {
    const resultPromises: Promise<void | TaskResult>[] = indexPackages.map(async indexPackage => {
      const urlPromises: Promise<string>[] = [];
      for (const idx of indexPackage) {
        urlPromises.push(urlCreator(idx));
      }
      return Promise.all(urlPromises).then(async urls => imagePreloader.add('id', urls));
    });

    return Promise.all(resultPromises);
  }
}

export default new ImagePreloadScheduler();
